import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useData } from "../context/DataContext";
import { useEcommerce } from "../hooks/useEcommerce";
import { useProductSchema } from "../hooks/useProductSchema";
import theme from "../styles/themes/theme";

// Product-specific attribute keys with API mappings
const PRODUCT_ATTRIBUTE_KEYS = {
  ML1000: {
    phrase: { code: "Phrase", template_code: "" },
    font: { code: "Font", template_code: "" },
    color: { code: "Baked_Enamel_Swatches", template_code: "Color" },
    hardware: { code: "Gemini_Minnesota_Hardware", template_code: "Hardware" },
    size: { code: "Size", template_code: "" },
  },
  BL1000: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "Paint_Color", template_code: "" },
    font: { code: "Font", template_code: "" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Mounting_Hardware", template_code: "Hardware" },
  },
  BL2000: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "Baked_Enamel_Colors", template_code: "Paint_Color" },
    font: { code: "Font", template_code: "" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    finish: { code: "Metal_Finish", template_code: "" },
  },
  BL2200: {
    phrase: { code: "Phrase", template_code: "" },
    font: { code: "Font", template_code: "" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    finish: { code: "Metal_Finish", template_code: "" },
  },
  BL31000: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "Baked_Enamel_Colors", template_code: "Paint_Color" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    thickness: { code: "Thickness", template_code: "" },
    finish: { code: "FCAL_Finishes", template_code: "Metal_Finish" },
  },
  BL32000: {
    phrase: { code: "Phrase", template_code: "" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    thickness: { code: "Thickness", template_code: "" },
    finish: { code: "FCBZ_Finishes", template_code: "Metal_Finish" },
  },
  BL33000: {
    phrase: { code: "Phrase", template_code: "" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    thickness: { code: "Thickness", template_code: "" },
    finish: { code: "FCBR_Finishes", template_code: "Metal_Finish" },
  },
  BL40100: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "FCAC_Color", template_code: "Color" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Mounting_Hardware_Acrylic", template_code: "Hardware" },
    thickness: { code: "Thickness", template_code: "" },
  },
  BL40200: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "FCMA_Finish", template_code: "Color" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Mounting_Hardware_Acrylic", template_code: "Hardware" },
    thickness: { code: "Thickness", template_code: "" },
  },
  BL70100: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "FCAC_Color", template_code: "Color" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
    size: { code: "Size", template_code: "" },
    hardware: { code: "Hardware", template_code: "" },
    thickness: { code: "Thickness", template_code: "" },
  },
  PFL100: {
    phrase: { code: "Phrase", template_code: "" },
    thickness: { code: "Foam_Thickness", template_code: "" },
    color: { code: "Color", template_code: "" },
    hardware: {
      code: "Hardware",
      template_code: "",
    },
    size: { code: "Size", template_code: "" },
    font: { code: "Fonts_Vinyl", template_code: "Font" },
  },
  // Default for unknown products
  DEFAULT: {
    phrase: { code: "Phrase", template_code: "" },
    color: { code: "Color", template_code: "" },
    font: { code: "Font", template_code: "" },
    size: { code: "Size", template_code: "" },
  },
};

// Configurable cart URL
const CART_URL = "https://www.alphabetsigns.com/cart-contents.html";

const AddToCart = () => {
  const {
    canvas,
    mivaGlobals,
    setSnackbar,
    setLoading,
    totalPrice,
    basketItem,
    selectedProduct,
    selectedFont,
    setSelectedFont,
    selectedColor,
    setSelectedColor,
    selectedSize,
    setSelectedSize,
    thickness,
    setThickness,
    finish,
    setFinish,
    hardware,
    setHardware,
    selectedPhrase,
    setSelectedPhrase,
    updatePriceAndDimensions,
    quantity,
    setQuantity,
  } = useData();
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const { mivaAddToCart } = useEcommerce();

  // Initialize productSchema
  const productSchema = useProductSchema(
    mivaGlobals,
    selectedFont,
    setSelectedFont,
    setSelectedColor,
    setSelectedSize,
    setThickness,
    setFinish,
    setHardware,
    selectedColor,
    selectedSize,
    thickness,
    finish,
    hardware
  );

  // Map color RGB to mivaname
  const getColorMivaName = useCallback(
    (rgb) => {
      if (!productSchema?.color?.options) return rgb;
      const colorObj = productSchema.color.options.find(
        (opt) => opt.rgb === rgb
      );
      return colorObj?.mivaname || rgb;
    },
    [productSchema]
  );

  // Add to Cart button is enabled when there is at least one line
  useEffect(() => {
    setIsDisabled(basketItem.lines.length === 0);
  }, [basketItem.lines.length]);

  // Handle add to cart click
  const handleAddToCartClick = useCallback(async () => {
    if (typeof setLoading !== "function") {
      setSnackbar({
        open: true,
        message: "Internal error: Loading state unavailable",
        severity: "error",
      });
      return;
    }

    setSpinnerLoading(true);
    setLoading(true);

    try {
      if (!mivaGlobals?.productcode) {
        throw new Error("E-commerce configuration is missing product code");
      }

      if (totalPrice <= 0 || !basketItem.lines.length) {
        throw new Error("Please add at least one line of text");
      }

      // Use default attribute keys if product code is unknown
      const attributeKeys =
        PRODUCT_ATTRIBUTE_KEYS[selectedProduct?.code] ||
        PRODUCT_ATTRIBUTE_KEYS.DEFAULT;
      if (!attributeKeys) {
        throw new Error(
          `No attribute keys defined for product ${
            selectedProduct?.code || "unknown"
          }`
        );
      }

      if (!productSchema || Object.keys(productSchema).length === 0) {
        throw new Error("Product schema is invalid or empty");
      }

      // Validate and submit each line separately
      const requiredAttributes = Object.keys(attributeKeys).filter(
        (key) => key !== "phrase"
      );
      console.log("[AddToCart]requiredAttributes: ", requiredAttributes);

      for (const [index, line] of basketItem.lines.entries()) {
        // Validate required attributes for the current line

        const missingAttributes = requiredAttributes
          .filter((key) => !line[key] || line[key] === "")
          .map((key) => ({ key, line: index + 1 }));
        console.log("[AddToCart]missingAttributes: ", missingAttributes);

        if (missingAttributes.length) {
          throw new Error(
            `Missing attributes: ${missingAttributes
              .map((attr) => `${attr.key} for line ${attr.line}`)
              .join(", ")}`
          );
        }

        // Create Product_Attributes for the current line using API mappings
        const productAttributes = Object.entries(attributeKeys)
          .map(([key, mapping]) => {
            const value =
              key === "phrase"
                ? line.phrase || ""
                : key === "color"
                ? getColorMivaName(line.color)
                : line[key] || "";
            return {
              code: mapping.code,
              value,
              Template_code: mapping.template_code,
            };
          })
          .filter((attr) => attr.value);

        console.log(
          "[AddToCart]before jsonBasket:productAttributes: ",
          productAttributes
        );

        const jsonBasket = {
          function: "Action_AddProductToBasket",
          Product_Code: mivaGlobals.productcode,
          Quantity: Number(quantity) || 1,
          update_price: Number(line.price || 0),
          Product_Attribute_Count: productAttributes.length,
          Product_Attributes: productAttributes,
          session_id: mivaGlobals.sessionid,
          basket_id: mivaGlobals.basketid,
        };

        console.log("[AddToCart]jsonBasket: ", jsonBasket);

        await mivaAddToCart(jsonBasket);
      }

      setSnackbar({
        open: true,
        message: "All lines added to cart successfully!",
        severity: "success",
      });

      window.location.href = CART_URL;
    } catch (error) {
      console.error("AddToCart: Failed to add to cart", error);
      const errorMessage = error.message.includes("session")
        ? "Session expired. Please log in again."
        : `Failed to add design to cart: ${error.message}`;
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setSpinnerLoading(false);
      setLoading(false);
    }
  }, [
    setLoading,
    setSnackbar,
    mivaGlobals,
    selectedProduct,
    totalPrice,
    basketItem,
    quantity,
    productSchema,
    mivaAddToCart,
    getColorMivaName,
  ]);

  return (
    <Stack
      direction="row"
      spacing={{
        xs: theme.spacing(0.5),
        sm: theme.spacing(1),
        md: theme.spacing(2),
      }}
      alignItems="center"
      role="region"
      aria-label="Add to cart controls"
    >
      {/* QUANTITY SELECTOR — RIGHT BEFORE THE BUTTON */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{ whiteSpace: "nowrap" }}
        >
          Quantity:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "grey.400",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <IconButton
            size="small"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
            sx={{ borderRadius: 0 }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body1"
            sx={{
              minWidth: 40,
              textAlign: "center",
              fontWeight: "bold",
              px: 1,
            }}
          >
            {quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setQuantity(quantity + 1)}
            sx={{ borderRadius: 0 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
      <span>
        <Button
          variant="contained"
          color="success"
          disabled={isDisabled}
          onClick={handleAddToCartClick}
          startIcon={
            spinnerLoading ? (
              <CircularProgress
                size={{ xs: "1rem", sm: "1.25rem", md: "1.5rem" }}
                color="inherit"
              />
            ) : (
              <ShoppingCartIcon
                sx={{
                  width: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                  height: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                }}
              />
            )
          }
          className="btn-gtm-ap"
          sx={{
            textTransform: "none",
            minWidth: { xs: "6rem", sm: "7.5rem", md: "9rem" },
            "&.Mui-disabled": {
              backgroundColor: "grey.300",
              color: "grey.600",
            },
          }}
          aria-label="Add to cart"
        >
          {spinnerLoading ? "Adding..." : "Add to Cart"}
        </Button>
      </span>
    </Stack>
  );
};

export default AddToCart;
